import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, coverUrl } from "../lib/api";
import { Clock, CheckCircle2, XCircle, AlertTriangle, ChevronDown, Inbox } from "lucide-react";
import { Link } from "react-router-dom";

const DEFAULT_COVER = "https://images.unsplash.com/photo-1659117675918-69ec794c64f2?crop=entropy&cs=srgb&fm=jpg&w=400&q=80";

const statusStyle = {
  pending: { icon: Clock, color: "text-[#C5A059]", bg: "bg-[#C5A059]/10", label: "Under review" },
  approved: { icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/10", label: "Approved" },
  rejected: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/10", label: "Rejected" },
};

// "Received Feb 3, 2:14pm" — friendlier than a raw ISO string. Falls back to
// the raw value if Date parsing fails so we never show "Invalid Date".
function formatReceivedAt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return `${date}, ${time.toLowerCase()}`;
  } catch { return null; }
}

export default function MySubmissionsPage() {
  const [items, setItems] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showFailures, setShowFailures] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, a] = await Promise.allSettled([
          api.get("/submissions/mine"),
          api.get("/submissions/mine/audit"),
        ]);
        if (cancelled) return;
        if (m.status === "fulfilled") setItems(m.value.data || []);
        else setErr(formatApiError(m.reason));
        // The audit endpoint may not exist on a yet-to-be-redeployed prod;
        // failing it should never block the page.
        if (a.status === "fulfilled") setAudit(a.value.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Map track_id → received_at from audit (for successful audit rows that
  // matched a saved track). Falls back to the track's own created_at if no
  // audit row is present (e.g. submissions made before the audit log shipped).
  const receivedByTrackId = useMemo(() => {
    const m = new Map();
    for (const a of audit) {
      if (a.success && a.track_id) m.set(a.track_id, a.created_at);
    }
    return m;
  }, [audit]);

  // Failed-attempt rows in the last 14 days, newest first. Capped to 5.
  const recentFailures = useMemo(() => {
    const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
    return audit
      .filter((a) => !a.success)
      .filter((a) => {
        if (!a.created_at) return true;
        const t = new Date(a.created_at).getTime();
        return isNaN(t) ? true : t >= cutoff;
      })
      .slice(0, 5);
  }, [audit]);

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#C5A059] mb-2">Your contributions</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] tracking-tight">My submissions</h1>
          <p className="text-[#A1A4AB] mt-2">Track the review status of everything you've submitted.</p>
        </div>
        <Link to="/submit" className="btn-primary" data-testid="mysubs-new-btn">Submit new</Link>
      </div>

      {err && <div className="text-red-400 text-sm">{err}</div>}

      {/* Recent failed attempts — only shown if there's something to surface */}
      {!loading && recentFailures.length > 0 && (
        <div
          className="rounded-lg border border-amber-500/30 bg-amber-500/[0.04] p-4"
          data-testid="mysubs-failed-attempts"
        >
          <button
            type="button"
            onClick={() => setShowFailures((v) => !v)}
            className="w-full flex items-center justify-between gap-2 text-left"
            data-testid="mysubs-failed-toggle"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-300" strokeWidth={1.5} />
              <span className="text-sm text-[#FDFBF7] font-medium">
                {recentFailures.length} recent upload{recentFailures.length === 1 ? "" : "s"} didn't go through
              </span>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-[#A1A4AB] transition-transform ${showFailures ? "rotate-180" : ""}`}
              strokeWidth={1.5}
            />
          </button>
          {showFailures && (
            <ul className="mt-3 space-y-2" data-testid="mysubs-failed-list">
              {recentFailures.map((f, i) => (
                <li
                  key={`${f.created_at}-${i}`}
                  className="text-xs flex items-start justify-between gap-3 border-t border-amber-500/15 pt-2 first:border-t-0 first:pt-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[#FDFBF7] truncate">
                      {f.title || "(no title)"} {f.artist && <span className="text-[#A1A4AB]">— {f.artist}</span>}
                    </div>
                    <div className="text-amber-200/70 mt-0.5 break-words">
                      {f.error || "Upload failed"}
                      {f.status_code && <span className="text-[#6C707A]"> · HTTP {f.status_code}</span>}
                    </div>
                  </div>
                  <div className="text-[10px] text-[#6C707A] whitespace-nowrap flex-shrink-0">
                    {formatReceivedAt(f.created_at) || ""}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-24 rounded-lg shimmer" />)}</div>
      ) : items.length === 0 ? (
        <div className="card-surface p-16 text-center">
          <Inbox className="w-8 h-8 text-[#C5A059] mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-[#FDFBF7] font-serif text-2xl mb-2">You haven't submitted anything yet</div>
          <div className="text-[#A1A4AB] mb-6">Your submissions will appear here once you upload.</div>
          <Link to="/submit" className="btn-primary" data-testid="mysubs-empty-cta">Submit your first track</Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="my-submissions-list">
          {items.map((s) => {
            const style = statusStyle[s.status] || statusStyle.pending;
            const Icon = style.icon;
            const cover = coverUrl(s) || DEFAULT_COVER;
            const receivedIso = receivedByTrackId.get(s.id) || s.created_at;
            const receivedLabel = formatReceivedAt(receivedIso);
            return (
              <div key={s.id} className="card-surface p-4 flex items-center gap-4" data-testid={`my-sub-${s.id}`}>
                <div className="w-16 h-16 rounded-md overflow-hidden border border-[#22262E] bg-[#111317] flex-shrink-0">
                  <img src={cover} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[#FDFBF7] font-medium truncate">{s.title}</div>
                  <div className="text-[#A1A4AB] text-sm truncate">{s.artist}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="overline text-[#6C707A]">{s.category}</span>
                    {s.genre && <span className="overline text-[#6C707A]">· {s.genre}</span>}
                    {receivedLabel && (
                      <span
                        className="overline text-[10px] text-[#6C707A] inline-flex items-center gap-1"
                        data-testid={`my-sub-received-${s.id}`}
                        title={`Received ${receivedIso}`}
                      >
                        · Received {receivedLabel}
                      </span>
                    )}
                  </div>
                  {s.admin_notes && (
                    <div className="text-xs text-[#A1A4AB] mt-2 p-2 rounded border border-[#22262E] bg-[#060709]/60">
                      <span className="overline text-[#C5A059]">Admin note </span>
                      {s.admin_notes}
                    </div>
                  )}
                </div>
                <div className={`flex items-center gap-1.5 text-xs ${style.color} ${style.bg} px-3 py-1.5 rounded-full`} data-testid={`my-sub-status-${s.id}`}>
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {style.label}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
